import React, { useState, useEffect } from 'react';
import { getQuestionGroups, createGameSession } from '../utils/gameApi';
import { getToken } from '../utils/auth';
import { THEMES, THEME_LABELS } from '../theme';
import './AdminGameCreate.css';

function AdminGameCreate({ onSessionCreated }) {
  const [questionGroups, setQuestionGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [ttlHours, setTtlHours] = useState(24);
  const [customCode, setCustomCode] = useState('');
  const [gameName, setGameName] = useState('CTTA Trivia');
  const [countdownSeconds, setCountdownSeconds] = useState(5);
  const [sessionTheme, setSessionTheme] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadQuestionGroups();
  }, []);

  const loadQuestionGroups = async () => {
    try {
      const data = await getQuestionGroups();
      setQuestionGroups(data.groups);
      if (data.groups.length > 0) {
        setSelectedGroup(data.groups[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading question groups:', error);
      setError('Failed to load question groups');
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!selectedGroup) {
      setError('Please select a question group');
      return;
    }

    // Validate TTL
    if (!ttlHours || ttlHours < 1 || ttlHours > 168) {
      setError('TTL must be between 1 and 168 hours (7 days)');
      return;
    }

    // Validate custom code if provided
    if (customCode) {
      const trimmedCode = customCode.trim();
      if (trimmedCode.length < 3 || trimmedCode.length > 10) {
        setError('Custom code must be 3-10 characters');
        return;
      }
      if (!/^[a-zA-Z0-9]+$/.test(trimmedCode)) {
        setError('Custom code must contain only letters and numbers');
        return;
      }
    }

    setCreating(true);
    setError('');

    try {
      const response = await createGameSession(selectedGroup, ttlHours, customCode.trim(), gameName.trim() || 'CTTA Trivia', countdownSeconds, sessionTheme);
      // Transform API response to match what AdminGameControl expects
      const sessionForControl = {
        code: response.sessionCode,
        joinUrl: response.joinUrl,
        qrCode: response.qrCode,
        ...response.sessionData
      };
      onSessionCreated(sessionForControl);
    } catch (error) {
      console.error('Error creating session:', error);
      setError(error.message || 'Failed to create game session');
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-game-create-container">
        <div className="loading">Loading question groups...</div>
      </div>
    );
  }

  return (
    <div className="admin-game-create-container">
      <h1 className="page-title">Create New Game</h1>

      <div className="create-card">
        <h2>Select Question Group</h2>

        {questionGroups.length === 0 ? (
          <div className="no-groups">
            <p>No question groups available.</p>
            <p>Please create a question group first.</p>
          </div>
        ) : (
          <>
            <div className="question-groups-list">
              {questionGroups.map((group) => (
                <div
                  key={group.id}
                  className={`group-card ${selectedGroup === group.id ? 'selected' : ''}`}
                  onClick={() => setSelectedGroup(group.id)}
                >
                  <h3>{group.name}</h3>
                  <p>{group.questions.length} questions</p>
                  <div className="question-types">
                    {group.questions.filter(q => q.type === 'multiple_choice').length} Multiple Choice,{' '}
                    {group.questions.filter(q => q.type === 'free_text').length} Free Text
                  </div>
                </div>
              ))}
            </div>

            <div className="ttl-configuration">
              <h3>Game Name</h3>
              <div className="ttl-input-group">
                <label htmlFor="game-name">Name:</label>
                <input
                  id="game-name"
                  type="text"
                  placeholder="e.g. CTTA Trivia"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  className="ttl-input"
                  maxLength="50"
                />
              </div>
              <p className="ttl-help-text">Shown on the welcome screen: "Welcome to {gameName || 'CTTA Trivia'}"</p>
            </div>

            <div className="ttl-configuration">
              <h3>Question Countdown</h3>
              <div className="ttl-input-group">
                <label htmlFor="countdown-seconds">Countdown:</label>
                <input
                  id="countdown-seconds"
                  type="number"
                  min="0"
                  max="15"
                  value={countdownSeconds}
                  onChange={(e) => setCountdownSeconds(Number(e.target.value))}
                  className="ttl-input"
                />
                <span className="ttl-unit">seconds</span>
              </div>
              <p className="ttl-help-text">Countdown shown before each question appears (0 to skip, max 15s)</p>
            </div>

            <div className="ttl-configuration">
              <h3>Game Code (Optional)</h3>
              <div className="ttl-input-group">
                <label htmlFor="custom-code">Custom Code:</label>
                <input
                  id="custom-code"
                  type="text"
                  placeholder="Leave empty for random (e.g., TAMIL, GAME1)"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                  className="ttl-input"
                  maxLength="10"
                />
              </div>
              <p className="ttl-help-text">
                {customCode
                  ? `Your game code will be: ${customCode.trim()}`
                  : 'Random 6-character code will be generated (e.g., A3B7K9)'}
              </p>
            </div>

            <div className="ttl-configuration">
              <h3>Game Theme (Optional)</h3>
              <div className="ttl-input-group">
                <label htmlFor="session-theme">Theme:</label>
                <select
                  id="session-theme"
                  value={sessionTheme}
                  onChange={(e) => setSessionTheme(e.target.value)}
                  className="ttl-input"
                >
                  <option value="">Use My Default Theme</option>
                  {THEMES.map(t => (
                    <option key={t} value={t}>{THEME_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              <p className="ttl-help-text">Override the color theme for this specific game session</p>
            </div>

            <div className="ttl-configuration">
              <h3>Session Duration</h3>
              <div className="ttl-input-group">
                <label htmlFor="ttl-hours">Time to Live (TTL):</label>
                <input
                  id="ttl-hours"
                  type="number"
                  min="1"
                  max="168"
                  value={ttlHours}
                  onChange={(e) => setTtlHours(Number(e.target.value))}
                  className="ttl-input"
                />
                <span className="ttl-unit">hours</span>
              </div>
              <p className="ttl-help-text">
                Session will expire after {ttlHours} hour{ttlHours !== 1 ? 's' : ''} ({Math.floor(ttlHours / 24)} day{Math.floor(ttlHours / 24) !== 1 ? 's' : ''} {ttlHours % 24 > 0 ? `${ttlHours % 24}h` : ''})
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              className="create-button"
              onClick={handleCreateSession}
              disabled={creating || !selectedGroup}
            >
              {creating ? 'Creating...' : 'Create Game Session'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminGameCreate;
